import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { notificationsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { GlobalSearch, SearchButton } from '../ui/global-search';
import { ThemeToggle } from '../ui/theme-toggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  Building2,
  FileText,
  Bell,
  Search,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ClipboardList,
  Mail,
  MessageSquare,
  Archive,
  ExternalLink,
  GraduationCap,
} from 'lucide-react';
import { getRoleLabel } from '../../lib/utils';

const PortalLayout = ({ children }) => {
  const { user, logout, isAdmin, isStaff } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Erreur chargement notifications:', error);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const staffLinks = [
    { href: '/portail', icon: LayoutDashboard, label: 'Tableau de bord' },
    { href: '/portail/clients', icon: Building2, label: 'Clients' },
    { href: '/portail/projets', icon: FolderOpen, label: 'Projets' },
    { href: '/portail/messages', icon: MessageSquare, label: 'Messagerie' },
    { href: '/portail/recherche', icon: Search, label: 'Recherche' },
    { href: '/portail/archives', icon: Archive, label: 'Archives' },
    { href: '/portail/journal', icon: ClipboardList, label: 'Journal' },
    { href: '/portail/contacts', icon: Mail, label: 'Contacts' },
  ];

  const clientLinks = [
    { href: '/portail', icon: LayoutDashboard, label: 'Mes projets' },
    { href: '/portail/messages', icon: MessageSquare, label: 'Messagerie' },
    { href: '/portail/recherche', icon: Search, label: 'Recherche' },
  ];

  const adminLinks = [
    { href: '/portail/utilisateurs', icon: Users, label: 'Utilisateurs', adminOnly: true },
    { href: '/cms', icon: Settings, label: 'Gestion du contenu', adminOnly: false },
    { href: '/academy', icon: GraduationCap, label: 'PEY aCADemie', adminOnly: false },
  ];

  const navLinks = isStaff ? staffLinks : clientLinks;
  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="portal-layout">
      {/* Top Navigation - Dark Header */}
      <header className="bg-[#1D1D1B] sticky top-0 z-50 shadow-lg">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg text-white transition-colors"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/portail" className="flex items-center gap-3">
              <img 
                src="/favicon.png" 
                alt="PEY" 
                className="w-9 h-9 object-contain"
              />
              <span className="font-heading font-semibold text-white hidden sm:block text-lg">
                Portail PEY
              </span>
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Search Button */}
            <SearchButton onClick={() => setSearchOpen(true)} />
            
            {/* Theme Toggle */}
            <ThemeToggle className="text-white/80 hover:bg-white/10" />
            
            {/* Notifications */}
            <Link 
              to="/portail/notifications" 
              className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5 text-white/80" />
              {unreadCount > 0 && (
                <span className="notification-badge" data-testid="notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 hover:bg-white/10 text-white"
                  data-testid="user-menu-btn"
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm text-[#1D1D1B]"
                    style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
                  >
                    {user?.prenom?.[0]}{user?.nom?.[0]}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-white">{user?.prenom} {user?.nom}</p>
                    <p className="text-xs text-white/60">{getRoleLabel(user?.role)}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-white/60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-none shadow-xl">
                <div className="px-3 py-2 sm:hidden border-b">
                  <p className="text-sm font-medium text-[#1D1D1B]">{user?.prenom} {user?.nom}</p>
                  <p className="text-xs text-neutral-500">{user?.email}</p>
                </div>
                <DropdownMenuItem asChild>
                  <Link to="/portail/profil" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Paramètres
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Dark Theme */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#1D1D1B] transform transition-transform duration-300 lg:transform-none ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
          data-testid="portal-sidebar"
        >
          <div className="flex flex-col h-full pt-20 lg:pt-6">
            {/* Logo for mobile */}
            <div className="lg:hidden px-6 pb-6 border-b border-white/10">
              <Link to="/portail" className="flex items-center gap-3">
                <img 
                  src="/favicon.png" 
                  alt="PEY" 
                  className="w-10 h-10 object-contain"
                />
                <span className="font-heading font-semibold text-white text-lg">
                  Portail PEY
                </span>
              </Link>
            </div>

            <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`sidebar-link ${isActive(link.href) ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`sidebar-${link.label.toLowerCase().replace(/[\s']/g, '-')}`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}

              {isStaff && (
                <>
                  <div className="pt-6 pb-2">
                    <p className="px-4 text-xs font-semibold text-white/40 uppercase tracking-wider">
                      Administration
                    </p>
                  </div>
                  {adminLinks
                    .filter((link) => !link.adminOnly || isAdmin)
                    .map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`sidebar-link ${isActive(link.href) ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`sidebar-admin-${link.label.toLowerCase().replace(/[\s']/g, '-')}`}
                    >
                      <link.icon className="w-5 h-5" />
                      {link.label}
                    </Link>
                  ))}
                </>
              )}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-white/10">
              <Link
                to="/"
                className="flex items-center gap-2 text-sm text-white/50 hover:text-[#FFD537] transition-colors group"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Retour au site public</span>
              </Link>
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-xs text-white/30">
                  © {new Date().getFullYear()} PEY Consultants
                </p>
                <p className="text-xs text-white/30 mt-1">
                  Développé par{' '}
                  <a 
                    href="https://www.dgsolutionscanada.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#FFD537]/50 hover:text-[#FFD537] transition-colors"
                  >
                    DG Solutions
                  </a>
                </p>
              </div>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-30 lg:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-4rem)] p-4 lg:p-8 bg-neutral-50 dark:bg-slate-900" data-testid="portal-main">
          {children}
        </main>
      </div>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default PortalLayout;
